import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import FormContainer from '../../components/ui/FormContainer';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { getLocationId, saveProgress, loadFormData, useCoalescedSave, AUTOSAVE_ON_CHANGE } from '../../utils/onboardingApi';

const BrandGuidelinesSetup = () => {
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadError, setUploadError] = useState('');
  const [uploadProgress, setUploadProgress] = useState('');
  const [driveUploadComplete, setDriveUploadComplete] = useState(false);
  const [formData, setFormData] = useState({
    primaryColor: '#3B82F6',
    secondaryColor: '#10B981',
    accentColor: '#F59E0B',
    brandTone: '',
    brandVoice: '',
    logoFile: null,
    secondaryLogoFile: null
  });

  const [errors, setErrors] = useState({});

  // Load saved form data from API on component mount
  useEffect(() => {
    // Try to load from API first
    loadFormData('brand-guidelines')
      .then(data => {
        if (Object.keys(data).length > 0) {
          setFormData(prev => ({
            ...prev,
            primaryColor: data?.primaryColor || prev?.primaryColor,
            secondaryColor: data?.secondaryColor || prev?.secondaryColor,
            accentColor: data?.accentColor || prev?.accentColor,
            brandTone: data?.brandTone || prev?.brandTone,
            brandVoice: data?.brandVoice || prev?.brandVoice,
            logoFile: data?.logoFile || null,
            secondaryLogoFile: data?.secondaryLogoFile || null
          }));
          // Check if loaded data has confirmed Drive uploads (fileId present)
          if (data?.logoFile?.fileId) {
            setDriveUploadComplete(true);
          }
        } else {
          // Fallback to localStorage for legacy data
          const savedData = localStorage.getItem('brandGuidelinesData');
          if (savedData) {
            try {
              const parsedData = JSON.parse(savedData);
              setFormData(prevData => ({
                ...prevData,
                ...parsedData,
                logoFile: parsedData?.logoFile || null,
                secondaryLogoFile: parsedData?.secondaryLogoFile || null
              }));
            } catch (error) {
              console.error('Failed to parse saved brand guidelines data:', error);
              localStorage.removeItem('brandGuidelinesData');
            }
          }
        }
      })
      .catch(err => {
        console.error('Error loading form data:', err);
        // Fallback to localStorage on API error
        const savedData = localStorage.getItem('brandGuidelinesData');
        if (savedData) {
          try {
            const parsedData = JSON.parse(savedData);
            setFormData(prevData => ({
              ...prevData,
              ...parsedData,
              logoFile: parsedData?.logoFile || null,
              secondaryLogoFile: parsedData?.secondaryLogoFile || null
            }));
          } catch (error) {
            console.error('Failed to parse saved brand guidelines data:', error);
          }
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  // Build the save payload from current formData. Shared by the legacy
  // autosave useEffect and the new useCoalescedSave hook.
  const buildPayload = () => {
    const serializeLogoFile = (file) => {
      if (!file) return null;
      const base = {
        name: file?.name,
        size: file?.size,
        type: file?.type
      };
      if (file?.fileId) base.fileId = file.fileId;
      if (file?.url) base.url = file.url;
      if (file?.filename) base.filename = file.filename;
      return base;
    };
    return {
      primaryColor: formData?.primaryColor,
      secondaryColor: formData?.secondaryColor,
      accentColor: formData?.accentColor,
      brandTone: formData?.brandTone,
      brandVoice: formData?.brandVoice,
      logoFile: serializeLogoFile(formData?.logoFile),
      secondaryLogoFile: serializeLogoFile(formData?.secondaryLogoFile)
    };
  };

  // NEW: coalesced-save behavior (blur + navigation + tab close).
  // Flip AUTOSAVE_ON_CHANGE in onboardingApi.js to revert to legacy autosave.
  const { handleBlur } = useCoalescedSave({
    step: 'brand-guidelines',
    buildPayload,
    isLoading
  });

  // LEGACY: autosave on every formData change (debounced 5s).
  // Disabled by default via AUTOSAVE_ON_CHANGE; kept for one-flag revert.
  useEffect(() => {
    if (!AUTOSAVE_ON_CHANGE) return;
    if (isLoading) return;

    const timeoutId = setTimeout(() => {
      const dataToSave = buildPayload();
      // Save to localStorage as backup
      localStorage.setItem('brandGuidelinesData', JSON.stringify(dataToSave));
      // Save to API (silent, fire-and-forget)
      saveProgress('brand-guidelines', dataToSave)
        .then(() => {})
        .catch(err => {
          console.error('Failed to auto-save brand guidelines:', err);
        });
    }, 5000); // Debounce for 5 seconds

    return () => clearTimeout(timeoutId);
  }, [formData, isLoading]);

  const handleBack = () => {
    navigate('/document-upload-step-2');
  };

  const handleColorChange = (colorType, value) => {
    setFormData(prev => ({
      ...prev,
      [colorType]: value
    }));
  };

  const handleFileUpload = (fileType, event) => {
    const file = event?.target?.files?.[0];
    setFormData(prev => ({
      ...prev,
      [fileType]: file
    }));
    // New file selected — needs uploading
    setDriveUploadComplete(false);
    setUploadError('');
    // Clear error when file is uploaded
    if (file && errors?.[fileType]) {
      setErrors(prev => ({
        ...prev,
        [fileType]: null
      }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (value && errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.logoFile) {
      newErrors.logoFile = 'Primary logo is required';
    }
    
    if (!formData?.brandTone?.trim()) {
      newErrors.brandTone = 'Brand tone is required';
    }
    
    if (!formData?.brandVoice?.trim()) {
      newErrors.brandVoice = 'Brand voice is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = () => {
    // Save current state as draft
    const dataToSave = {
      ...formData,
      logoFile: formData?.logoFile ? { 
        name: formData?.logoFile?.name, 
        size: formData?.logoFile?.size,
        type: formData?.logoFile?.type
      } : null,
      secondaryLogoFile: formData?.secondaryLogoFile ? { 
        name: formData?.secondaryLogoFile?.name, 
        size: formData?.secondaryLogoFile?.size,
        type: formData?.secondaryLogoFile?.type
      } : null
    };
    localStorage.setItem('brandGuidelinesData', JSON.stringify(dataToSave));
    // Navigate back to confirmation or dashboard
    navigate('/deals-dashboard-empty-state');
  };

  const handleSaveAndContinue = async () => {
    if (!validateForm()) {
      return;
    }

    setUploadError('');

    // If documents were already successfully uploaded to Drive, skip re-upload
    if (driveUploadComplete) {
      const hasNewFiles = [formData?.logoFile, formData?.secondaryLogoFile].some(
        f => f && f instanceof File
      );
      if (!hasNewFiles) {
        navigate('/business-information-form');
        return;
      }
    }

    // Check for stale metadata - files that aren't File objects and weren't confirmed uploaded
    const logoFileKeys = ['logoFile', 'secondaryLogoFile'];
    const hasStaleFiles = logoFileKeys.some(key => {
      const file = formData?.[key];
      if (!file) return false;
      if (file instanceof File) return false;
      // It's metadata - only OK if it has a fileId (confirmed uploaded to Drive)
      return !file.fileId;
    });

    if (hasStaleFiles) {
      setUploadError('Logo file(s) need to be re-selected. Please remove and re-upload them.');
      return;
    }

    // Upload logo files to Google Drive (Inbox folder) before continuing
    const logoFileEntries = logoFileKeys
      .filter(key => formData?.[key] && formData[key] instanceof File)
      .map(key => ({ key, file: formData[key] }));

    if (logoFileEntries.length > 0) {
      setIsUploading(true);
      setUploadProgress('Uploading logo files...');
      try {
        const uploadFormData = new FormData();
        const locationId = getLocationId();
        uploadFormData.append('locationId', locationId);
        uploadFormData.append('folderPath', 'Inbox');
        logoFileEntries.forEach(({ file }) => {
          uploadFormData.append('files', file);
        });

        const response = await fetch(
          'https://raiseai-api-production.up.railway.app/api/media/upload-to-drive',
          {
            method: 'POST',
            body: uploadFormData
          }
        );

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }

        const result = await response.json();

        // Map uploaded file URLs back to logo fields
        const updatedFormData = { ...formData };
        logoFileEntries.forEach(({ key, file }, index) => {
          const uploadResult = result.results?.[index];
          if (uploadResult?.success) {
            updatedFormData[key] = {
              name: file.name,
              size: file.size,
              type: file.type,
              url: uploadResult.url,
              fileId: uploadResult.fileId,
              filename: uploadResult.filename
            };
          }
        });

        // Also preserve any already-confirmed files
        logoFileKeys.forEach(key => {
          if (formData?.[key]?.fileId && !updatedFormData[key]?.fileId) {
            updatedFormData[key] = formData[key];
          }
        });

        setFormData(updatedFormData);
        setDriveUploadComplete(true);
        setUploadProgress('Upload complete!');

        // Save confirmed data with fileIds to API
        const serializeLogoFile = (file) => {
          if (!file) return null;
          const base = { name: file?.name, size: file?.size, type: file?.type };
          if (file?.fileId) base.fileId = file.fileId;
          if (file?.url) base.url = file.url;
          if (file?.filename) base.filename = file.filename;
          return base;
        };

        const dataToSave = {
          primaryColor: updatedFormData?.primaryColor,
          secondaryColor: updatedFormData?.secondaryColor,
          accentColor: updatedFormData?.accentColor,
          brandTone: updatedFormData?.brandTone,
          brandVoice: updatedFormData?.brandVoice,
          logoFile: serializeLogoFile(updatedFormData?.logoFile),
          secondaryLogoFile: serializeLogoFile(updatedFormData?.secondaryLogoFile)
        };

        await saveProgress('brand-guidelines', dataToSave);
        localStorage.setItem('brandGuidelines', JSON.stringify(dataToSave));
        localStorage.removeItem('brandGuidelinesData');

        navigate('/business-information-form');
      } catch (error) {
        console.error('Error uploading logo files to Google Drive:', error);
        setUploadError('Failed to upload logo files. Please try again.');
        setUploadProgress('');
      } finally {
        setIsUploading(false);
      }
    } else {
      // No new files to upload — just save and navigate
      const serializeLogoFile = (file) => {
        if (!file) return null;
        const base = { name: file?.name, size: file?.size, type: file?.type };
        if (file?.fileId) base.fileId = file.fileId;
        if (file?.url) base.url = file.url;
        if (file?.filename) base.filename = file.filename;
        return base;
      };

      const dataToSave = {
        primaryColor: formData?.primaryColor,
        secondaryColor: formData?.secondaryColor,
        accentColor: formData?.accentColor,
        brandTone: formData?.brandTone,
        brandVoice: formData?.brandVoice,
        logoFile: serializeLogoFile(formData?.logoFile),
        secondaryLogoFile: serializeLogoFile(formData?.secondaryLogoFile)
      };
      localStorage.setItem('brandGuidelines', JSON.stringify(dataToSave));

      try {
        await saveProgress('brand-guidelines', dataToSave);
      } catch (err) {
        console.error('Failed to save brand guidelines to API:', err);
      }

      localStorage.removeItem('brandGuidelinesData');
      navigate('/business-information-form');
    }
  };

  return (
    <FormContainer maxWidth="max-w-4xl">
      {/* onBlur on wrapper catches blur events bubbling from any child input,
          driving the new coalesced save on field exit. */}
      <div className="space-y-8" onBlur={handleBlur}>
        {/* Header */}
        <div className="space-y-4">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Brand Guidelines Setup</h1>
            <p className="text-gray-600 mt-2">Configure your brand identity for investor materials</p>
          </div>
        </div>

        {/* Logo Upload Section */}
        <div className="bg-white rounded-lg border-2 border-dashed border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Logo Upload</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Primary Logo */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-700">
                Primary Logo <span className="text-red-500">*</span>
              </h3>
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer ${
                  errors?.logoFile ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                onClick={() => document.getElementById('primary-logo')?.click()}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload('logoFile', e)}
                  className="hidden"
                  id="primary-logo"
                />
                {formData?.logoFile ? (
                  <div className="space-y-2">
                    <div className="w-16 h-16 bg-blue-100 rounded-lg mx-auto flex items-center justify-center">
                      <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-gray-900">{formData?.logoFile?.name}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFileUpload('logoFile', { target: { files: [null] } });
                      }}
                    >
                      Replace
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-600">Upload your primary logo</p>
                    <p className="text-xs text-gray-400 mt-1">Click to upload or drag and drop</p>
                  </div>
                )}
              </div>
              {errors?.logoFile && (
                <p className="text-sm text-red-600">{errors?.logoFile}</p>
              )}
              <p className="text-xs text-gray-500">Recommended: PNG or SVG format, transparent background</p>
            </div>

            {/* Secondary Logo */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-700">Secondary Logo (Optional)</h3>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
                onClick={() => document.getElementById('secondary-logo')?.click()}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload('secondaryLogoFile', e)}
                  className="hidden"
                  id="secondary-logo"
                />
                {formData?.secondaryLogoFile ? (
                  <div className="space-y-2">
                    <div className="w-16 h-16 bg-blue-100 rounded-lg mx-auto flex items-center justify-center">
                      <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-gray-900">{formData?.secondaryLogoFile?.name}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFileUpload('secondaryLogoFile', { target: { files: [null] } });
                      }}
                    >
                      Replace
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-600">Upload secondary logo variation</p>
                    <p className="text-xs text-gray-400 mt-1">Click to upload or drag and drop</p>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">Alternative logo for different contexts</p>
            </div>
          </div>
        </div>

        {/* Color Palette Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Brand Colors</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Primary Color */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">Primary Color</label>
              <div className="flex items-center space-x-3">
                <div 
                  className="w-12 h-12 rounded-lg border-2 border-gray-200 cursor-pointer"
                  style={{ backgroundColor: formData?.primaryColor }}
                  onClick={() => document.getElementById('primary-color')?.click()}
                />
                <input
                  type="color"
                  id="primary-color"
                  value={formData?.primaryColor}
                  onChange={(e) => handleColorChange('primaryColor', e?.target?.value)}
                  className="sr-only"
                />
                <Input
                  type="text"
                  value={formData?.primaryColor}
                  onChange={(e) => handleColorChange('primaryColor', e?.target?.value)}
                  onClick={() => document.getElementById('primary-color')?.click()}
                  className="flex-1 uppercase font-mono cursor-pointer"
                  placeholder="#3B82F6"
                  readOnly
                />
              </div>
              <p className="text-xs text-gray-500">Main brand color for headers, buttons</p>
            </div>

            {/* Secondary Color */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">Secondary Color</label>
              <div className="flex items-center space-x-3">
                <div 
                  className="w-12 h-12 rounded-lg border-2 border-gray-200 cursor-pointer"
                  style={{ backgroundColor: formData?.secondaryColor }}
                  onClick={() => document.getElementById('secondary-color')?.click()}
                />
                <input
                  type="color"
                  id="secondary-color"
                  value={formData?.secondaryColor}
                  onChange={(e) => handleColorChange('secondaryColor', e?.target?.value)}
                  className="sr-only"
                />
                <Input
                  type="text"
                  value={formData?.secondaryColor}
                  onChange={(e) => handleColorChange('secondaryColor', e?.target?.value)}
                  onClick={() => document.getElementById('secondary-color')?.click()}
                  className="flex-1 uppercase font-mono cursor-pointer"
                  placeholder="#10B981"
                  readOnly
                />
              </div>
              <p className="text-xs text-gray-500">Supporting color for accents, highlights</p>
            </div>

            {/* Accent Color */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">Accent Color</label>
              <div className="flex items-center space-x-3">
                <div 
                  className="w-12 h-12 rounded-lg border-2 border-gray-200 cursor-pointer"
                  style={{ backgroundColor: formData?.accentColor }}
                  onClick={() => document.getElementById('accent-color')?.click()}
                />
                <input
                  type="color"
                  id="accent-color"
                  value={formData?.accentColor}
                  onChange={(e) => handleColorChange('accentColor', e?.target?.value)}
                  className="sr-only"
                />
                <Input
                  type="text"
                  value={formData?.accentColor}
                  onChange={(e) => handleColorChange('accentColor', e?.target?.value)}
                  onClick={() => document.getElementById('accent-color')?.click()}
                  className="flex-1 uppercase font-mono cursor-pointer"
                  placeholder="#F59E0B"
                  readOnly
                />
              </div>
              <p className="text-xs text-gray-500">Accent color for call-to-actions</p>
            </div>
          </div>
        </div>

        {/* Brand Voice & Tone Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Brand Voice & Tone</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Brand Tone */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Brand Tone <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData?.brandTone}
                onChange={(e) => handleInputChange('brandTone', e?.target?.value)}
                className={`w-full h-32 p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                  errors?.brandTone ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Describe your brand's tone (e.g., Professional, approachable, innovative, trustworthy...)"
              />
              {errors?.brandTone && (
                <p className="text-sm text-red-600">{errors?.brandTone}</p>
              )}
              <p className="text-xs text-gray-500">How should your brand sound? (e.g., formal, casual, friendly)</p>
            </div>

            {/* Brand Voice */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Brand Voice <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData?.brandVoice}
                onChange={(e) => handleInputChange('brandVoice', e?.target?.value)}
                className={`w-full h-32 p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                  errors?.brandVoice ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Describe your brand's voice characteristics and communication style..."
              />
              {errors?.brandVoice && (
                <p className="text-sm text-red-600">{errors?.brandVoice}</p>
              )}
              <p className="text-xs text-gray-500">What personality does your brand have when communicating?</p>
            </div>
          </div>
        </div>

        {/* Upload Error */}
        {uploadError && (
          <div className="flex items-center space-x-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
            <span>{uploadError}</span>
          </div>
        )}

        {/* Upload Progress */}
        {isUploading && uploadProgress && (
          <div className="flex items-center space-x-3 text-blue-600 text-sm bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span>{uploadProgress}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="pt-6">
          <Button
            onClick={handleSaveAndContinue}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            size="lg"
            disabled={isUploading}
          >
            {isUploading ? 'Uploading...' : 'Continue'}
          </Button>
        </div>
      </div>
    </FormContainer>
  );
};

export default BrandGuidelinesSetup;